/**
 * Who may open the application at all — the two-tier operator model.
 *
 * The unit runs on two kinds of account and no others:
 *
 * - **מנהל על** (`super_admin`, D1 `admin`) — every screen, the user directory,
 *   the codes and the system configuration.
 * - **מורשה / מפקד** (`admin`, D1 `manager`) — the whole operational day:
 *   משימות, רכבים and חיילים, their statuses and their real-time updates. Never
 *   the directory, never the configuration.
 *
 * A **נהג never signs in.** The roster still holds them — they are who a task is
 * assigned to, who a convoy is built from and whose name is printed on the
 * dispatch order — but the order reaches them as a shared image, not as a login.
 * There is nothing for a driver to open, so there is no door: {@link canSignIn}
 * refuses the `soldier` role before a session is ever written, and
 * `src/db/auth-store.ts` refuses it again at the point the session would be
 * created, so no screen can talk its way past the gate by skipping the flow.
 *
 * This module is the *rule*, stated once. `src/core/permissions.ts` still
 * answers "may X do Y?" for everything inside the app; this answers the one
 * question that comes before it — "may X be in here at all?".
 *
 * Pure: no Dexie, no network, no browser. The verification harness executes the
 * decisions here directly.
 */
import { isAccountUsable } from './permissions';
import type { User, UserRole } from '../types/user';

/** The two roles that may hold a session, widest first. */
export const OPERATOR_ROLES: readonly UserRole[] = ['super_admin', 'admin'];

/** How the unit names the two tiers on a sign-in screen. */
export const OPERATOR_ROLE_LABELS: Record<'super_admin' | 'admin', string> = {
  super_admin: 'מנהל על',
  admin: 'מורשה / מפקד',
};

/**
 * Every reason a sign-in is refused, in the words the screen shows.
 *
 * `notOperator` is the one this module exists for: it is what a נהג's number is
 * told, and it says so plainly rather than pretending the account is unknown —
 * the roster is not a secret, and "your number is not registered" would send
 * somebody to a מנהל על to fix an account that is working exactly as intended.
 */
export const SIGN_IN_REFUSALS = {
  unknown: 'המספר אינו רשום במערכת',
  inactive: 'החשבון אינו פעיל — פנו למנהל העל',
  notOperator: 'הכניסה שמורה למנהלים ולמורשים בלבד — נהגים מקבלים פקודת משימה בוואטסאפ',
} as const;

export type SignInRefusal = keyof typeof SIGN_IN_REFUSALS;

/** True for one of the two roles the system is open to. */
export const isOperatorRole = (role: string | null | undefined): boolean =>
  OPERATOR_ROLES.includes(String(role ?? '').trim() as UserRole);

/** True when the account is one of the two operator tiers. */
export const isOperator = (user: User | null | undefined): boolean =>
  Boolean(user) && isOperatorRole(user?.role);

/**
 * Why this account may not sign in, or `null` when it may.
 *
 * The order is deliberate: an account nobody holds is `unknown`, an operator
 * account that was disabled is `inactive`, and only a live account that is
 * simply not an operator gets the driver's message. A disabled מפקד must be
 * told their access was switched off, not that they are a driver.
 */
export function signInRefusal(user: User | null | undefined): SignInRefusal | null {
  if (!user) return 'unknown';
  if (!isOperatorRole(user.role)) return 'notOperator';
  if (!isAccountUsable(user)) return 'inactive';
  return null;
}

/** True when this account may hold a session. */
export const canSignIn = (user: User | null | undefined): boolean => signInRefusal(user) === null;

/** The sentence a refusal is reported with, or `null` when there is none. */
export function signInRefusalMessage(user: User | null | undefined): string | null {
  const refusal = signInRefusal(user);
  return refusal ? SIGN_IN_REFUSALS[refusal] : null;
}

/** Raised by `startSession()` when a non-operator account reaches the gate. */
export class NotAnOperatorError extends Error {
  readonly role: string;

  constructor(role: string) {
    super(SIGN_IN_REFUSALS.notOperator);
    this.name = 'NotAnOperatorError';
    this.role = role;
  }
}
